/*!
 * Ambaroud — generative scent atmospheres.
 * One shared WebGL context renders every [data-gl] canvas on the page (hero, product
 * atmosphere, scent-family tiles…) and copies each frame into that canvas' 2D context.
 * Each canvas picks a mode (smoke, embers, aqua, petals, gourmand, citrus, golddust,
 * musk, candy), a 3-colour palette and a seed, so every product gets its own sky.
 */
(function () {
  'use strict';
  var MODES = { smoke: 0, embers: 1, aqua: 2, petals: 3, gourmand: 4, citrus: 5, golddust: 6, musk: 7, candy: 8 };
  var reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  var coarse = window.matchMedia && matchMedia('(pointer: coarse)').matches;

  var VS = 'attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}';
  var FS = [
    'precision mediump float;',
    'uniform vec2 uRes;uniform float uTime;uniform vec2 uMouse;uniform vec3 uA;uniform vec3 uB;uniform vec3 uC;',
    'uniform float uMode;uniform float uSeed;uniform float uInt;uniform float uHover;',
    'float h21(vec2 p){p=fract(p*vec2(233.34,851.73));p+=dot(p,p+23.45);return fract(p.x*p.y);}',
    'vec2 h22(vec2 p){float n=h21(p);return vec2(n,h21(p+n+.37));}',
    'float noise(vec2 p){vec2 i=floor(p),f=fract(p);vec2 u=f*f*(3.-2.*f);',
    ' return mix(mix(h21(i),h21(i+vec2(1.,0.)),u.x),mix(h21(i+vec2(0.,1.)),h21(i+vec2(1.,1.)),u.x),u.y);}',
    'float fbm(vec2 p){float v=0.,a=.5;mat2 m=mat2(1.6,1.2,-1.2,1.6);for(int i=0;i<5;i++){v+=a*noise(p);p=m*p;a*=.5;}return v;}',
    // soft particle field: shape 0 dot, 1 petal, 2 bubble ring
    'float field(vec2 p,float sc,float vel,float sz,float sd,float dens,float shape){',
    ' p*=sc;p.y-=uTime*vel;p.x+=sin(p.y*.35+sd)*.45;',
    ' vec2 id=floor(p);vec2 f=fract(p)-.5;float r=h21(id+sd);',
    ' if(r>dens)return 0.;',
    ' vec2 o=(h22(id+sd*1.7)-.5)*.7;o+=vec2(sin(uTime*.9+r*40.),cos(uTime*.7+r*30.))*.08;',
    ' vec2 d=f-o;float tw=.55+.45*sin(uTime*(1.5+r*3.)+r*60.);float s=sz*(.55+r*1.2);',
    ' if(shape>1.5){float l=length(d);return smoothstep(s,s*.72,l)*smoothstep(s*.35,s*.72,l)*1.4+smoothstep(s*.5,0.,length(d-vec2(-s*.3,s*.3)))*.5;}',
    ' if(shape>.5){float a=uTime*(.25+r*.6)+r*6.28;mat2 R=mat2(cos(a),-sin(a),sin(a),cos(a));d=R*d;d.x*=2.3;return smoothstep(s,s*.15,length(d))*.95;}',
    ' return smoothstep(s,0.,length(d))*tw;}',
    'void main(){',
    ' vec2 uv=gl_FragCoord.xy/uRes;float asp=uRes.x/uRes.y;',
    ' vec2 p=(uv-.5)*vec2(asp,1.);vec2 m=(uMouse-.5)*vec2(asp,1.);float md=length(p-m);',
    ' float t=uTime*.055;int mode=int(uMode+.5);',
    ' vec2 sp=p+(p-m)*.14*exp(-md*md*5.)*uHover+vec2(uSeed*.37,uSeed*.21);',
    ' float rise=(mode==0||mode==1)?1.7:(mode==2?.5:(mode==4?.35:.85));',
    ' vec2 q=vec2(fbm(sp*1.3+vec2(0.,-t*rise)),fbm(sp*1.3+vec2(5.2,1.3)+t));',
    ' vec2 r=vec2(fbm(sp*1.5+2.8*q+vec2(1.7,9.2)+t*1.3),fbm(sp*1.5+2.8*q+vec2(8.3,2.8)-t*.9));',
    ' float f=fbm(sp*1.1+3.2*r-vec2(0.,t*rise));',
    ' vec3 col=uA;',
    ' if(mode==0){',
    '  float w=smoothstep(.34,.98,f);col=mix(uA,uB*.55+uA*.45,w*w);',
    '  float rg=1.-abs(fbm(sp*1.9+4.2*r+vec2(0.,-t*2.))*2.-1.);col+=uC*pow(rg,12.)*.55*(.4+.6*w);',
    '  col+=uC*pow(f,5.)*1.7*(.55+.45*length(r));col+=uB*.1*smoothstep(.55,.85,q.x);',
    ' }else if(mode==1){',
    '  float w=smoothstep(.36,.98,f);col=mix(uA,uB*.55+uA*.45,w);col+=uC*pow(f,6.)*1.3;',
    '  float rg=1.-abs(fbm(sp*1.7+4.*r+vec2(0.,-t*2.))*2.-1.);col+=mix(uB,uC,.5)*pow(rg,14.)*.4;',
    '  float e=field(p,9.,.95,.11,uSeed,.22,0.)+field(p,5.,.55,.09,uSeed+3.1,.16,0.)*1.2;',
    '  col+=mix(uB,uC,.6)*e*2.*(.55+.45*uv.y);',
    ' }else if(mode==2){',
    '  col=mix(uA,uB*.62+uA*.38,smoothstep(.22,.92,f));',
    '  vec2 cp=p*3.4+vec2(t*2.2,-t*1.4)+r;float cr=1.-abs(noise(cp)*2.-1.);float cr2=1.-abs(noise(cp*1.9+3.)*2.-1.);',
    '  col+=uC*(pow(cr,7.)+pow(cr2,9.)*.6)*.38*(.35+uv.y*.8);',
    '  float ray=pow(max(0.,sin((p.x+p.y*.35)*7.+t*5.)*.5+.5),10.)*smoothstep(.2,1.,uv.y)*.16;col+=uC*ray;',
    '  col+=uC*field(p,8.,.32,.1,uSeed,.28,2.)*.55;',
    ' }else if(mode==3){',
    '  col=mix(uA,mix(uA,uB,.55),smoothstep(.3,.92,f));col+=uC*pow(f,4.)*.75;',
    '  float pt=field(p,4.6,-.22,.12,uSeed,.34,1.)+field(p,8.,-.3,.08,uSeed+7.,.22,1.)*.7;',
    '  col=mix(col,mix(uB,uC,.45+.3*sin(uTime+p.x)),clamp(pt,0.,1.)*.72);',
    ' }else if(mode==4){',
    '  float a=t*1.4+length(p)*1.2;mat2 R=mat2(cos(a),-sin(a),sin(a),cos(a));',
    '  float sw=fbm(R*p*1.35+3.6*r);float bands=sin(sw*15.+t*7.)*.5+.5;',
    '  col=mix(uA,uB*.72+uA*.28,smoothstep(.28,.82,sw));',
    '  col+=uC*pow(bands,14.)*.4*smoothstep(.35,.7,sw);col+=uC*pow(sw,5.)*.7;',
    '  col+=uC*field(p,15.,.12,.04,uSeed,.18,0.)*.9;',
    ' }else if(mode==5){',
    '  vec2 sun=vec2(.34*asp,.36);vec2 dv=p-sun;float sd=length(dv);float an=atan(dv.y,dv.x);',
    '  float rays=pow(abs(sin(an*5.+t*1.6+sin(an*3.+t)*.6)),3.)*exp(-sd*1.5)*.16;float gw=exp(-sd*1.8)*.5;',
    '  col=mix(uA,uB*.5+uA*.5,smoothstep(.3,.9,f));col+=uC*(gw+rays)*(.7+.3*f);',
    '  col+=mix(uB,uC,.5)*field(p,10.,.42,.06,uSeed,.2,0.)*1.25;',
    ' }else if(mode==6){',
    '  col=mix(uA,uB*.48+uA*.52,smoothstep(.25,.95,f));col+=uC*pow(f,6.)*1.3;',
    '  float d=field(p,17.,.16,.035,uSeed,.34,0.)+field(p,8.,.1,.05,uSeed+5.,.2,0.)*1.1+field(p,4.,.06,.05,uSeed+9.,.12,0.)*1.4;',
    '  col+=uC*d*1.35;',
    ' }else if(mode==7){',
    '  float cl=smoothstep(.2,.95,f);col=mix(uA,mix(uA,uC,.32),cl);col+=uB*.14*cl;',
    '  col+=uC*pow(f,3.)*.25;col+=uC*field(p,30.,.05,.025,uSeed,.4,0.)*.32;',
    ' }else{',
    '  col=mix(uA,uB*.5+uA*.5,smoothstep(.3,.9,f));',
    '  float b1=field(p,3.6,.14,.24,uSeed,.5,0.);float b2=field(p,5.,.2,.18,uSeed+4.,.42,0.);',
    '  col+=uB*b1*.5+uC*b2*.45;col+=uC*field(p,18.,.85,.03,uSeed+2.,.3,0.)*.95;',
    ' }',
    ' col+=uC*.09*exp(-md*md*7.)*uHover;',
    ' col=mix(uA,col,uInt);',
    ' vec2 vv=uv-.5;col*=1.-dot(vv,vv)*.75;',
    ' col+=(h21(gl_FragCoord.xy+fract(uTime*.5)*91.)-.5)*.03;',
    ' gl_FragColor=vec4(col,1.);',
    '}'
  ].join('\n');

  var gl, glc, prog, U = {}, targets = [], raf = 0, t0 = performance.now(), frameNo = 0, io;

  function hex(h) {
    h = (h || '').trim().replace('#', '');
    if (h.length === 3) h = h.split('').map(function (c) { return c + c; }).join('');
    var n = parseInt(h, 16) || 0;
    return [(n >> 16 & 255) / 255, (n >> 8 & 255) / 255, (n & 255) / 255];
  }
  function seedOf(s) {
    var h = 2166136261;
    s = String(s || 'amb');
    for (var i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
    return ((h >>> 0) % 1000) / 37.0;
  }
  function sh(type, src) {
    var s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) { console.warn('[amb-gl]', gl.getShaderInfoLog(s)); return null; }
    return s;
  }
  function init() {
    if (window.NexusSpeed && window.NexusSpeed.lite) return false; // nexus:speed-pack: phones get the theme's static .gl-fallback atmosphere
    glc = document.createElement('canvas');
    glc.width = 2; glc.height = 2;
    var o = { alpha: false, antialias: false, depth: false, stencil: false, premultipliedAlpha: false, preserveDrawingBuffer: false, powerPreference: 'low-power' };
    gl = glc.getContext('webgl', o) || glc.getContext('experimental-webgl', o);
    if (!gl) return false;
    var v = sh(gl.VERTEX_SHADER, VS), f = sh(gl.FRAGMENT_SHADER, FS);
    if (!v || !f) return false;
    prog = gl.createProgram(); gl.attachShader(prog, v); gl.attachShader(prog, f); gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return false;
    gl.useProgram(prog);
    var b = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, b);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(prog, 'p'); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    ['uRes', 'uTime', 'uMouse', 'uA', 'uB', 'uC', 'uMode', 'uSeed', 'uInt', 'uHover'].forEach(function (k) { U[k] = gl.getUniformLocation(prog, k); });
    glc.addEventListener('webglcontextlost', function (e) { e.preventDefault(); cancelAnimationFrame(raf); raf = 0; targets.forEach(function (t) { t.c.parentNode && t.c.parentNode.classList.add('gl-fallback'); }); }, false);
    io = new IntersectionObserver(function (es) {
      es.forEach(function (e) { var t = e.target.__amb; if (t) { t.vis = e.isIntersecting; if (t.vis) kick(); } });
    }, { rootMargin: '120px' });
    return true;
  }

  function attach(c) {
    if (c.__amb) return c.__amb;
    if (!gl && !init()) { c.parentNode && c.parentNode.classList.add('gl-fallback'); c.style.display = 'none'; return null; }
    var pal = (c.getAttribute('data-palette') || '#0b0806,#b8893a,#f3dfae').split(',');
    var t = {
      c: c, ctx: c.getContext('2d', { alpha: false }),
      mode: MODES[c.getAttribute('data-mode')] != null ? MODES[c.getAttribute('data-mode')] : 0,
      a: hex(pal[0]), b: hex(pal[1] || pal[0]), g: hex(pal[2] || pal[1] || pal[0]),
      seed: seedOf(c.getAttribute('data-seed')),
      scale: parseFloat(c.getAttribute('data-scale') || (coarse ? '.42' : '.55')),
      inter: c.hasAttribute('data-interactive'), hoverEl: c.closest('[data-gl-hover]'),
      int: parseFloat(c.getAttribute('data-intensity') || '1'), speed: parseFloat(c.getAttribute('data-speed') || '1'),
      vis: false, mx: .5, my: .5, tx: .5, ty: .5, hov: 0, hovT: c.hasAttribute('data-interactive') ? 1 : 0, w: 0, h: 0, painted: false
    };
    c.__amb = t;
    if (t.hoverEl) {
      t.hoverEl.addEventListener('pointerenter', function () { t.hovT = 1; t.speed = t.speedBase * 2.2; });
      t.hoverEl.addEventListener('pointerleave', function () { t.hovT = 0; t.speed = t.speedBase; });
      t.hoverEl.addEventListener('pointermove', function (e) { var r = c.getBoundingClientRect(); t.tx = (e.clientX - r.left) / r.width; t.ty = 1 - (e.clientY - r.top) / r.height; });
    }
    t.speedBase = t.speed;
    targets.push(t); io.observe(c);
    return t;
  }

  window.addEventListener('pointermove', function (e) {
    for (var i = 0; i < targets.length; i++) {
      var t = targets[i]; if (!t.inter || !t.vis) continue;
      var r = t.c.getBoundingClientRect();
      t.tx = (e.clientX - r.left) / r.width; t.ty = 1 - (e.clientY - r.top) / r.height;
    }
  }, { passive: true });

  function size(t) {
    var r = t.c.getBoundingClientRect();
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var w = Math.max(2, Math.round(r.width * dpr * t.scale)), h = Math.max(2, Math.round(r.height * dpr * t.scale));
    var cap = coarse ? 560 : 1100;
    if (w > cap) { h = Math.round(h * cap / w); w = cap; }
    if (t.c.width !== w || t.c.height !== h) { t.c.width = w; t.c.height = h; t.painted = false; }
    t.w = w; t.h = h;
  }

  function draw(t, time) {
    t.mx += (t.tx - t.mx) * .05; t.my += (t.ty - t.my) * .05; t.hov += (t.hovT - t.hov) * .06;
    gl.viewport(0, 0, t.w, t.h);
    gl.uniform2f(U.uRes, t.w, t.h);
    gl.uniform1f(U.uTime, time * t.speed + t.seed * 13.0);
    gl.uniform2f(U.uMouse, t.mx, t.my);
    gl.uniform3fv(U.uA, t.a); gl.uniform3fv(U.uB, t.b); gl.uniform3fv(U.uC, t.g);
    gl.uniform1f(U.uMode, t.mode); gl.uniform1f(U.uSeed, t.seed); gl.uniform1f(U.uInt, t.int); gl.uniform1f(U.uHover, t.hov);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    t.ctx.drawImage(glc, 0, glc.height - t.h, t.w, t.h, 0, 0, t.w, t.h);
    t.painted = true;
  }

  function frame(now) {
    raf = 0;
    if (document.hidden) return;
    var vis = targets.filter(function (t) { return t.vis && t.c.isConnected && !t.c.closest('[data-mnav]:not(.is-open)'); }); // nexus:speed-pack: the closed menu's canvas is not drawn
    if (!vis.length) return;
    frameNo++;
    var many = vis.length > 2;
    if (!(many && (frameNo & 1))) {
      var mw = 0, mh = 0;
      vis.forEach(function (t) { size(t); mw = Math.max(mw, t.w); mh = Math.max(mh, t.h); });
      if (glc.width < mw || glc.height < mh) { glc.width = Math.max(glc.width, mw); glc.height = Math.max(glc.height, mh); }
      var time = (now - t0) / 1000;
      for (var i = 0; i < vis.length; i++) {
        if (reduce && vis[i].painted) continue;
        draw(vis[i], reduce ? 4.0 : time);
      }
    }
    if (!reduce) raf = requestAnimationFrame(frame);
  }
  function kick() { if (!raf) raf = requestAnimationFrame(frame); }
  document.addEventListener('visibilitychange', function () { if (!document.hidden) kick(); });
  window.addEventListener('resize', function () { targets.forEach(function (t) { t.painted = false; }); kick(); }, { passive: true });

  function scan(root) {
    (root || document).querySelectorAll('canvas[data-gl]').forEach(function (c) { attach(c); });
    kick();
  }

  window.AmbGL = { scan: scan, attach: attach, kick: kick, modes: MODES };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { scan(); });
  else scan();
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
})();
